import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { openAuthedFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck, Plus, Pencil, Trash2, Loader2, Users, Printer } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", description: "", responsibilities: "", parent_poste_id: null, permissions: [], active: true };

export default function PostesPage() {
  const [postes, setPostes] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [dlg, setDlg] = useState(null); // {mode, id?}
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [p, c] = await Promise.allSettled([api.get("/rbac/postes"), api.get("/rbac/permissions")]);
    if (p.status === "fulfilled") setPostes(p.value.data);
    if (c.status === "fulfilled") setCatalog(c.value.data);
  };
  useEffect(() => { load(); }, []);

  const byCategory = catalog.reduce((acc, p) => { (acc[p.category] = acc[p.category] || []).push(p); return acc; }, {});
  const permLabel = (k) => catalog.find(p => p.key === k)?.label || k;

  const togglePerm = (key) => setForm(f => ({
    ...f, permissions: f.permissions.includes(key) ? f.permissions.filter(x => x !== key) : [...f.permissions, key],
  }));

  const save = async () => {
    setSaving(true);
    try {
      if (dlg.mode === "edit") await api.put(`/rbac/postes/${dlg.id}`, form);
      else await api.post("/rbac/postes", form);
      toast.success(dlg.mode === "edit" ? "Poste mis à jour" : "Poste créé");
      setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le poste "${p.name}" ?`)) return;
    try {
      await api.delete(`/rbac/postes/${p.id}`);
      toast.success("Poste supprimé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="postes-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><ShieldCheck className="h-3 w-3" /><span>POSTES & HABILITATIONS</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Postes & habilitations</h1>
          <p className="text-slate-500 mt-1">Créez librement les postes de votre organigramme et attribuez-leur des pouvoirs — sans limite, sans intervention technique.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => openAuthedFile("/rbac/permissions-matrix/pdf").catch(() => toast.error("Impossible de générer la matrice PDF"))} className="gap-1 border-[#1E3A8A] text-[#1E3A8A] hover:bg-blue-50" data-testid="print-matrix-btn"><Printer className="h-4 w-4" />Imprimer la matrice</Button>
          <Button onClick={() => { setForm(EMPTY); setDlg({ mode: "create" }); }} className="bg-[#1E3A8A] text-white gap-1" data-testid="add-poste-btn"><Plus className="h-4 w-4" />Nouveau poste</Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {postes.map(p => (
          <div key={p.id} className={`bg-white rounded-2xl border border-slate-200 shadow-sm p-5 ${!p.active ? "opacity-50" : ""}`} data-testid={`poste-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-heading font-bold text-lg text-[#1E3A8A]">{p.name}</div>
                {p.parent_poste_id && <div className="text-xs text-slate-400">Rattaché à : {postes.find(x => x.id === p.parent_poste_id)?.name || "—"}</div>}
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => { setForm({ ...EMPTY, ...p }); setDlg({ mode: "edit", id: p.id }); }} data-testid={`poste-edit-${p.id}`}><Pencil className="h-3 w-3" /></Button>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(p)} data-testid={`poste-del-${p.id}`}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
            {p.responsibilities && <p className="text-xs text-slate-500 mt-2">{p.responsibilities}</p>}
            <div className="flex flex-wrap gap-1 mt-3">
              {(p.permissions || []).slice(0, 6).map(k => <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-[#1E3A8A] font-semibold">{permLabel(k)}</span>)}
              {(p.permissions || []).length > 6 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">+{p.permissions.length - 6} autres</span>}
              {(p.permissions || []).length === 0 && <span className="text-[10px] text-slate-400">Aucune habilitation</span>}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-1 text-xs text-slate-500">
              <Users className="h-3 w-3" />
              {(p.holders || []).length ? (p.holders.map(h => h.full_name || h.email).join(", ")) : "Aucun titulaire"}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{dlg?.mode === "edit" ? `Modifier — ${form.name}` : "Nouveau poste"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nom du poste *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex : Directeur du Primaire" data-testid="poste-name" />
              </div>
              <div>
                <Label>Supérieur hiérarchique</Label>
                <Select value={form.parent_poste_id || "none"} onValueChange={v => setForm({ ...form, parent_poste_id: v === "none" ? null : v })}>
                  <SelectTrigger data-testid="poste-parent"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Aucun (sommet) —</SelectItem>
                    {postes.filter(p => p.id !== dlg?.id).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Responsabilités</Label>
              <Textarea rows={2} value={form.responsibilities} onChange={e => setForm({ ...form, responsibilities: e.target.value })} placeholder="Missions principales de ce poste..." data-testid="poste-resp" />
            </div>
            <div>
              <Label className="mb-2 block">Habilitations ({form.permissions.length} sélectionnée{form.permissions.length > 1 ? "s" : ""})</Label>
              <div className="space-y-3 border border-slate-200 rounded-xl p-3 max-h-72 overflow-y-auto">
                {Object.entries(byCategory).map(([cat, perms]) => (
                  <div key={cat}>
                    <div className="text-xs font-bold uppercase text-slate-500 mb-1.5">{cat}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {perms.map(p => (
                        <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox checked={form.permissions.includes(p.key)} onCheckedChange={() => togglePerm(p.key)} data-testid={`perm-${p.key}`} />
                          <span>{p.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1E3A8A] text-white" data-testid="poste-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
